import type { QueryClient } from '@tanstack/react-query'

export const ASSISTANT_DATA_CHANGED_EVENT = 'comforthub:assistant-data-changed'

export type AssistantDataChangedDetail = {
  confirmed: boolean
  toolName?: string
  args?: Record<string, unknown>
  resultSummary?: string | null
}

/** Broadcast so open pages (e.g. Generate Shifts) can sync selection + refetch. */
export function notifyAssistantDataChanged(detail: AssistantDataChangedDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ASSISTANT_DATA_CHANGED_EVENT, { detail }))
}

/** Invalidate React Query caches affected by assistant Confirm writes. */
export function invalidateAfterAssistantConfirm(queryClient: QueryClient, toolName?: string) {
  const name = toolName ?? ''
  // Always refresh schedule/hours caches — cheap and avoids draft/timeline staleness.
  void queryClient.invalidateQueries({ queryKey: ['generate-saved-shifts'] })
  void queryClient.invalidateQueries({ queryKey: ['shift-month-overages'] })
  void queryClient.invalidateQueries({ queryKey: ['shifts'] })
  void queryClient.invalidateQueries({ queryKey: ['staff'] })

  // Catalog + commerce: invalidate whenever the tool name hints at a write, and also
  // for unknown tools so Confirm never leaves open pages showing stale lists.
  const catalogish =
    !name ||
    name.includes('Order') ||
    name.includes('Stock') ||
    name.includes('Inventory') ||
    name.includes('Category') ||
    name.includes('Offering') ||
    name.includes('Addon') ||
    name.includes('Store') ||
    name.includes('Plan') ||
    name.includes('Booking') ||
    name.includes('Event')

  if (catalogish) {
    void queryClient.invalidateQueries({ queryKey: ['orders'] })
    void queryClient.invalidateQueries({ queryKey: ['stock'] })
    void queryClient.invalidateQueries({ queryKey: ['inventory'] })
    void queryClient.invalidateQueries({ queryKey: ['offerings'] })
    void queryClient.invalidateQueries({ queryKey: ['stores'] })
    void queryClient.invalidateQueries({ queryKey: ['categories'] })
    void queryClient.invalidateQueries({ queryKey: ['plans'] })
    void queryClient.invalidateQueries({ queryKey: ['bookings'] })
    void queryClient.invalidateQueries({ queryKey: ['addons'] })
    void queryClient.invalidateQueries({ queryKey: ['events'] })
  }

  if (!name || name.includes('Metabase') || name.includes('Report')) {
    void queryClient.invalidateQueries({ queryKey: ['reports'] })
    // Legacy key kept until every page has moved to ['reports'].
    void queryClient.invalidateQueries({ queryKey: ['metabase-reports'] })
  }
}
